import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Alert, Box, Chip, CircularProgress, Divider, IconButton, Stack, Tooltip, Typography } from '@pulse/ui';
import { useState } from 'react';

import { fmtChange, orderChangeKeys } from '../../../lib/audit-diff.util';
import { formatDdMmYyyyHm } from '../../../lib/date.util';
import { useIssueHistory } from '../hooks/useIssues';

const PAGE_SIZE = 20;

// Shared by IssueDetailDialog.tsx's "History" tab and IssueLinkPage.tsx's "History" section —
// both surfaces show the exact same activity feed for the same issue, so the rendering (and its
// pagination) lives once here rather than twice. Backed by GET .../history, which reuses the
// same audit trail every other Issue mutation already writes to (see IssueService.getHistory()'s
// own comment on the API side for why this is a project:read-gated route, not the admin-only
// Audit Log page's admin:read-gated one).
export function IssueHistoryPanel({ projectId, issueId }: { projectId: string; issueId: string }) {
  const [page, setPage] = useState(0);
  const { data, isLoading, isError } = useIssueHistory(projectId, issueId, PAGE_SIZE, page * PAGE_SIZE);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (isError) return <Alert severity="error">Failed to load history.</Alert>;

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  if (total === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No activity recorded yet.
      </Typography>
    );
  }

  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, from + items.length - 1);

  return (
    <Stack spacing={0}>
      {items.map((entry, i) => {
        const changes = entry.changes;
        const keys = changes ? orderChangeKeys(changes) : [];
        return (
          <Box key={entry.id}>
            <Stack direction="row" spacing={1.5} sx={{ py: 1.25 }}>
              <Chip
                size="small"
                label={entry.action === 'CREATE' ? 'Created' : 'Updated'}
                color={entry.action === 'CREATE' ? 'success' : 'info'}
                sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0, mt: 0.25 }}
              />
              <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
                  <Typography variant="body2" fontWeight={500}>
                    {entry.actorName ?? 'System'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDdMmYyyyHm(entry.createdAt)}
                  </Typography>
                </Stack>
                {keys.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {entry.action === 'CREATE' ? 'Created this issue.' : 'No field changes recorded.'}
                  </Typography>
                ) : (
                  keys.map((k) => {
                    const line = fmtChange(k, changes![k]!.old, changes![k]!.new);
                    // workLog's label embeds its own description (up to 5000 characters — see
                    // CreateIssueWorkLogDto), unlike every other diffed field here, which is short
                    // enough to sit safely as a plain unbounded line. Scrollable, same as the
                    // Work Log tab's own description box, so a long note can't blow out this
                    // entry's height.
                    if (k === 'workLog') {
                      return (
                        <Box key={k} sx={{ maxHeight: 130, overflowY: 'auto' }}>
                          <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                            {line}
                          </Typography>
                        </Box>
                      );
                    }
                    return (
                      <Typography key={k} variant="body2" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                        {line}
                      </Typography>
                    );
                  })
                )}
              </Stack>
            </Stack>
            {i < items.length - 1 && <Divider />}
          </Box>
        );
      })}
      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ pt: 1.5 }}>
        <Typography variant="caption" color="text.secondary">
          {from}–{to} of {total}
        </Typography>
        <Tooltip title="Newer">
          <span>
            <IconButton size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)} aria-label="Newer entries">
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Older">
          <span>
            <IconButton size="small" disabled={to >= total} onClick={() => setPage((p) => p + 1)} aria-label="Older entries">
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    </Stack>
  );
}
